import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import subCategoryService from "../../services/subCategoryService";

// Async thunks for subcategory operations
export const fetchSubCategories = createAsyncThunk(
  "subCategories/fetchSubCategories",
  async (params, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.getAllSubCategories(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategories"
      );
    }
  }
);

export const fetchSubCategoryById = createAsyncThunk(
  "subCategories/fetchSubCategoryById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.getSubCategoryById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategory"
      );
    }
  }
);

export const fetchSubCategoriesByCategoryId = createAsyncThunk(
  "subCategories/fetchSubCategoriesByCategoryId",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.getSubCategoriesByCategoryId(
        categoryId
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch subcategories for category"
      );
    }
  }
);

export const createSubCategory = createAsyncThunk(
  "subCategories/createSubCategory",
  async ({ subCategoryData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.createSubCategory(
        subCategoryData,
        imageFile
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create subcategory"
      );
    }
  }
);

export const updateSubCategory = createAsyncThunk(
  "subCategories/updateSubCategory",
  async ({ id, subCategoryData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.updateSubCategory(
        id,
        subCategoryData,
        imageFile
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update subcategory"
      );
    }
  }
);

export const deleteSubCategory = createAsyncThunk(
  "subCategories/deleteSubCategory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.deleteSubCategory(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete subcategory"
      );
    }
  }
);

export const updateSubCategoryStatus = createAsyncThunk(
  "subCategories/updateSubCategoryStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.updateSubCategoryStatus(
        id,
        status
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update subcategory status"
      );
    }
  }
);

export const fetchSubCategoryStats = createAsyncThunk(
  "subCategories/fetchSubCategoryStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await subCategoryService.getSubCategoryStats();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategory stats"
      );
    }
  }
);

const subCategorySlice = createSlice({
  name: "subCategories",
  initialState: {
    subCategories: [],
    currentSubCategory: null,
    stats: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      status: "",
      name: "",
      category_id: "",
    },
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: "",
        name: "",
        category_id: "",
      };
    },
    setCurrentSubCategory: (state, action) => {
      state.currentSubCategory = action.payload;
    },
    clearCurrentSubCategory: (state) => {
      state.currentSubCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subcategories
      .addCase(fetchSubCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle different response formats from backend
        const responseData = action.payload.data || action.payload;
        const data = responseData.subCategories || responseData;
        state.subCategories = Array.isArray(data) ? data : [];
        state.pagination = responseData.pagination || {
          page: 1,
          limit: 10,
          total: Array.isArray(data) ? data.length : 0,
        };
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch subcategory by ID
      .addCase(fetchSubCategoryById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubCategoryById.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.currentSubCategory = responseData.subCategory || responseData;
      })
      .addCase(fetchSubCategoryById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch subcategories by category ID
      .addCase(fetchSubCategoriesByCategoryId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubCategoriesByCategoryId.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        const data = responseData.subCategories || responseData;
        state.subCategories = Array.isArray(data) ? data : [];
      })
      .addCase(fetchSubCategoriesByCategoryId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create subcategory
      .addCase(createSubCategory.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createSubCategory.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newSubCategory = responseData.subCategory || responseData;
        if (newSubCategory) {
          state.subCategories.unshift(newSubCategory);
        }
        state.success =
          action.payload.message || "Subcategory created successfully";
      })
      .addCase(createSubCategory.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update subcategory
      .addCase(updateSubCategory.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSubCategory.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedSubCategory = responseData.subCategory || responseData;
        const index = state.subCategories.findIndex(
          (subCategory) => subCategory.id === updatedSubCategory.id
        );
        if (index !== -1) {
          state.subCategories[index] = updatedSubCategory;
        }
        if (state.currentSubCategory?.id === updatedSubCategory.id) {
          state.currentSubCategory = updatedSubCategory;
        }
        state.success =
          action.payload.message || "Subcategory updated successfully";
      })
      .addCase(updateSubCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete subcategory
      .addCase(deleteSubCategory.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.subCategories = state.subCategories.filter(
          (subCategory) => subCategory.id !== action.payload.id
        );
        state.success =
          action.payload.response.message || "Subcategory deleted successfully";
      })
      .addCase(deleteSubCategory.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // Update subcategory status
      .addCase(updateSubCategoryStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSubCategoryStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedSubCategory = responseData.subCategory || responseData;
        const index = state.subCategories.findIndex(
          (subCategory) => subCategory.id === updatedSubCategory.id
        );
        if (index !== -1) {
          state.subCategories[index] = updatedSubCategory;
        }
        if (state.currentSubCategory?.id === updatedSubCategory.id) {
          state.currentSubCategory = updatedSubCategory;
        }
        state.success =
          action.payload.message || "Subcategory status updated successfully";
      })
      .addCase(updateSubCategoryStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Fetch subcategory stats
      .addCase(fetchSubCategoryStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubCategoryStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.data || action.payload;
      })
      .addCase(fetchSubCategoryStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setFilters,
  clearFilters,
  setCurrentSubCategory,
  clearCurrentSubCategory,
} = subCategorySlice.actions;

export default subCategorySlice.reducer;
